import React from 'react';
import { useContent } from '../hooks/useContent';

const getFaviconUrl = (link: string): string => {
  try {
    const url = new URL(link);
    return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=64`;
  } catch {
    return '';
  }
};

// Extracts the site name from a URL, e.g. "farmskins.com" → "farmskins"
const getSiteNameInitial = (link: string): string => {
  try {
    const hostname = new URL(link).hostname.replace('www.', '');
    const siteName = hostname.split('.')[0]; // "farmskins" from "farmskins.com"
    return siteName.charAt(0).toUpperCase();
  } catch {
    return '?';
  }
};

const RewardsPage: React.FC = () => {
    const { content, loading } = useContent();

    if (loading) {
        return (
            <div className="min-h-screen w-full flex items-center justify-center">
                <div className="text-white text-xl">Loading rewards...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen w-full">
            <div className="max-w-5xl mx-auto py-8 sm:py-10 px-4">
                <h1 className="text-2xl sm:text-4xl font-bold mb-6 sm:mb-8 text-white tracking-wider text-center">
                    Rewards
                </h1>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    {/* Left Box - Rewards (unchanged) */}
                    <div className="bg-[#23232b] rounded-xl p-4 sm:p-6 shadow border border-[#35353f] flex flex-col items-center text-center">
                        {content.rewards && content.rewards.length > 0 ? (
                            content.rewards.map((reward) => (
                                <div key={reward.id} className="w-full">
                                    <h2 className="text-base sm:text-lg font-bold text-gray-100 mb-4">
                                        {reward.title}
                                    </h2>
                                    {reward.imageUrl && (
                                        <div className="w-full mb-4">
                                            <img
                                                src={reward.imageUrl}
                                                alt={reward.title}
                                                className="w-full h-auto rounded"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).style.display = 'none';
                                                }}
                                            />
                                        </div>
                                    )}
                                    {reward.link && (
                                        <a
                                            href={reward.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-red-500 hover:text-red-500 visited:text-red-500 no-underline"
                                        >
                                            View Giveaway
                                        </a>
                                    )}
                                </div>
                            ))
                        ) : (
                            <h2 className="text-base sm:text-lg font-bold text-gray-100 mb-4">
                                No rewards available
                            </h2>
                        )}
                    </div>

                    {/* Right Box - Sponsors */}
                    <div className="bg-[#18181e] rounded-xl p-4 sm:p-6 shadow border border-[#35353f] flex flex-col">
                        <h2 className="text-base sm:text-lg font-bold text-gray-100 mb-4 text-center">Also Visit</h2>

                        {content.sponsors && content.sponsors.length > 0 ? (
                            <ul className="flex flex-col gap-3 list-none">
                                {content.sponsors.map((sponsor) => {
                                    const faviconUrl = getFaviconUrl(sponsor.link);
                                    return (
                                        <li key={sponsor.id}>
                                            <a
                                                href={sponsor.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="no-underline group"
                                            >
                                                <div className="flex items-center gap-3 rounded-xl px-3 py-3 border border-[#2e2e38] bg-[#1e1e26] transition-all duration-200 group-hover:border-[#ff2a2a55] group-hover:bg-[#23151599] group-hover:shadow-[0_0_12px_#ff2a2a33]">

                                                    {/* Favicon thumbnail from the sponsor's actual site */}
                                                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-[#35353f] bg-[#111116] flex items-center justify-center">
                                                        {faviconUrl ? (
                                                            <img
                                                                src={faviconUrl}
                                                                alt={sponsor.label}
                                                                className="w-7 h-7 object-contain"
                                                                onError={(e) => {
                                                                    
                                                                    (e.target as HTMLImageElement).style.display = 'none';
                                                                    (e.target as HTMLImageElement).parentElement!.innerHTML =
                                                                        `<span style="color:#ff2a2a;font-weight:bold;font-size:14px">${getSiteNameInitial(sponsor.link)}</span>`;
                                                                }}
                                                            />
                                                        ) : (
                                                            <span className="text-[#ff2a2a] font-bold text-sm">
                                                                {getSiteNameInitial(sponsor.link)}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Label */}
                                                    <span className="flex-1 text-center text-sm sm:text-base text-gray-200 font-medium group-hover:text-white transition-colors">
                                                        {sponsor.label}
                                                    </span>

                                                    {/* Chevron */}
                                                    <svg
                                                        className="w-4 h-4 text-[#555] flex-shrink-0 group-hover:text-[#ff2a2a] transition-colors"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </div>
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <p className="text-gray-400 text-center">No Links available</p>
                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};

export default RewardsPage;